// client/src/pages/ClientsPage.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  TextField,
  Grid,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import ClientForm from '../components/Clients/ClientForm';
import axios from '../api/axiosConfig';

// API functions
const fetchClients = async () => {
  const { data } = await axios.get('/clients');
  return data;
};

const deleteClient = async (id) => {
  await axios.delete(`/clients/${id}`);
};

const fetchClientStatistics = async (id) => {
  const { data } = await axios.get(`/clients/${id}/statistics`);
  return data;
};

const ClientsPage = () => {
  const [openForm, setOpenForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });
  const [statsDialog, setStatsDialog] = useState({ open: false, client: null, stats: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  
  const queryClient = useQueryClient();

  const { data: clients, isLoading, error } = useQuery({
    queryKey: ['clients'],
    queryFn: fetchClients,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteClient,
    onSuccess: () => {
      queryClient.invalidateQueries(['clients']);
      setSnackbar({ open: true, message: 'Client deleted successfully', severity: 'success' });
      setDeleteDialog({ open: false, id: null, name: '' });
    },
    onError: (err) => {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Delete failed', severity: 'error' });
    },
  });

  const handleViewStats = async (client) => {
    setStatsDialog({ open: true, client, stats: null });
    try {
      const stats = await fetchClientStatistics(client._id);
      setStatsDialog(prev => ({ ...prev, stats }));
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleDelete = () => {
    if (deleteDialog.id) {
      deleteMutation.mutate(deleteDialog.id);
    }
  };

  // Filter clients by search term
  const filteredClients = clients?.filter(client =>
    client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">Error loading clients: {error.message}</Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Clients Management
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Manage your clients, track their vehicles and devices
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingClient(null);
              setOpenForm(true);
            }}
          >
            Add Client
          </Button>
        </Box>

        {/* Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            label="Search clients"
            variant="outlined"
            placeholder="Search by name, contact person or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />
        </Paper>

        {/* Clients Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell><strong>Company Name</strong></TableCell>
                <TableCell><strong>Contact Person</strong></TableCell>
                <TableCell><strong>Email</strong></TableCell>
                <TableCell><strong>Phone</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClients?.map((client) => (
                <TableRow key={client._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon color="primary" fontSize="small" />
                      <Typography fontWeight={500}>{client.name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{client.contactPerson || '-'}</TableCell>
                  <TableCell>
                    {client.email ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">{client.email}</Typography>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {client.phone ? (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">{client.phone}</Typography>
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={client.isActive ? 'Active' : 'Inactive'}
                      color={client.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleViewStats(client)}
                      title="View Statistics"
                    >
                      📊
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditingClient(client);
                        setOpenForm(true);
                      }}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteDialog({ open: true, id: client._id, name: client.name })}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredClients?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No clients found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Client Form Modal */}
        <ClientForm
          open={openForm}
          onClose={() => {
            setOpenForm(false);
            setEditingClient(null);
          }}
          client={editingClient}
          onSuccess={() => {
            queryClient.invalidateQueries(['clients']);
            setSnackbar({ open: true, message: 'Client saved successfully', severity: 'success' });
          }}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null, name: '' })}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete client <strong>{deleteDialog.name}</strong>?
              <br />
              <Typography variant="caption" color="error">
                This action cannot be undone.
              </Typography>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, id: null, name: '' })}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Statistics Dialog */}
        <Dialog
          open={statsDialog.open}
          onClose={() => setStatsDialog({ open: false, client: null, stats: null })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Statistics: {statsDialog.client?.name}
          </DialogTitle>
          <DialogContent>
            {!statsDialog.stats ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="primary" align="center">
                        {statsDialog.stats.statistics?.vehicles || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" align="center">
                        Vehicles
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="primary" align="center">
                        {statsDialog.stats.statistics?.devices || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" align="center">
                        Active Devices
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="primary" align="center">
                        {statsDialog.stats.statistics?.workOrders || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" align="center">
                        Total Work Orders
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="primary" align="center">
                        {statsDialog.stats.statistics?.completedWorkOrders || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" align="center">
                        Completed Work Orders
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="warning.main" align="center">
                        {statsDialog.stats.statistics?.openTickets || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" align="center">
                        Open Tickets
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="primary" align="center">
                        {statsDialog.stats.statistics?.users || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" align="center">
                        Users
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatsDialog({ open: false, client: null, stats: null })}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
};

export default ClientsPage;